import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  InlineStack,
  Flex,
  Stack,
  Toggle,
  Button,
  Tooltip,
  Box,
  TextLabel,
  Link,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { FLIGHTDECK_BASE_URL } from '@/utils/flightdeck';
import { isEmptyString } from '@/utils/string';
import { useMasq, useHasMailServers, useWorkspaceDetails, useAccountUsers } from './hooks';
import { MasqLink } from './components/masq-link';
import { generatePath } from '@/services/router/generate-path';
import { MasqForm } from './components/masq-form';
import { MasqAccessPopover } from './components/masq-access-popover';
import { AccessSource, formatDataCenter, formatShortDate } from './utils/constants';
import * as styles from './index.css';

function getSupportAccessValue(
  hasReadMasqAccess: boolean,
  isSuperUser: boolean,
  accessGrantedUntil: number | undefined,
): string {
  if (hasReadMasqAccess && (accessGrantedUntil ?? 0) > Math.floor(Date.now() / 1000)) {
    const date = formatShortDate(accessGrantedUntil);
    return !isEmptyString(date) ? date : 'No access';
  }
  if (isSuperUser) return 'Full access';
  return 'No access';
}

function getSuccessAccessValue(
  hasReadMasqAccess: boolean,
  successTeamAccessEnabled: boolean,
  csAccessGrantedUntil: number | undefined,
): string {
  if ((csAccessGrantedUntil ?? 0) === -1 && hasReadMasqAccess) return 'Indefinite';
  if (successTeamAccessEnabled && hasReadMasqAccess) {
    const date = formatShortDate(csAccessGrantedUntil);
    return !isEmptyString(date) ? date : 'No access';
  }
  return 'No access';
}

function getIdentifiersDisplay(
  details: { identifier_type?: string; identifiers?: { cio_id: number; id: number; email: number } } | null | undefined,
): string {
  if (details == null) return '';
  if (details.identifier_type === 'email') return 'email';
  if (details.identifiers != null) {
    return Object.entries(details.identifiers)
      .filter(([k, v]) => k !== 'cio_id' && v !== 0)
      .map(([k]) => k)
      .join(', ');
  }
  return details.identifier_type ?? '';
}

export function MasqueradeBanner() {
  const auth = useAuth();
  const workspace = useCurrentWorkspace();
  const masq = useMasq();
  const themeContext = useThemeContext();
  const [isAccountFormVisible, setIsAccountFormVisible] = useState(false);

  useEffect(() => {
    themeContext.setVariable(GlobalCSSVariable.MasqBannerHeight, masq.isAllowed ? styles.MASQ_BANNER_HEIGHT : '0px');

    return () => {
      themeContext.setVariable(GlobalCSSVariable.MasqBannerHeight, '0px');
    };
  }, [masq.isAllowed, themeContext]);

  const { account, user } = auth;
  const workspaceDetails = useWorkspaceDetails(workspace?.id ?? undefined);
  const { usersById } = useAccountUsers(masq.isMasqOn ? account.id : undefined);

  const resolveUser = useCallback(
    (userId: string | undefined) => {
      if (userId == null || usersById == null) return userId;
      const found = usersById.get(userId);
      return found != null ? found.email : userId;
    },
    [usersById],
  );

  const mailServersQuery = useHasMailServers(
    workspace?.id != null ? String(workspace.id) : undefined,
    account.account_state,
  );

  const handleKeyUp = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsAccountFormVisible(false);
    }
  }, []);

  const { setReadOnly } = masq;
  const handleReadOnlyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setReadOnly(!event.currentTarget.checked);
    },
    [setReadOnly],
  );

  const showAccountForm = useCallback(() => setIsAccountFormVisible(true), []);
  const hideAccountForm = useCallback(() => setIsAccountFormVisible(false), []);

  const accountStateStyle = styles.accountStateStyles[account.account_state] ?? '';
  const supportAccessValue = getSupportAccessValue(
    user.hasReadMasqAccess,
    user.isSuperUser,
    account.access_granted_until,
  );
  const successAccessValue = getSuccessAccessValue(
    user.hasReadMasqAccess,
    account.success_team_access_enabled,
    account.cs_access_granted_until,
  );
  const smtpValue = mailServersQuery.isLoading
    ? 'loading'
    : mailServersQuery.data != null
      ? String(mailServersQuery.data)
      : 'N/A';
  const dataCenterDisplay = formatDataCenter(account.data_center);

  if (!masq.isAllowed) {
    return null;
  }

  if (masq.isMasqOn) {
    return (
      <Box
        role="toolbar"
        aria-label="Masquerade controls"
        px="100"
        py="050"
        flexShrink="0"
        className={clsx(styles.masqueradeBanner, accountStateStyle)}
        data-test-masquerade-banner="enabled"
        onKeyUp={handleKeyUp}
      >
        <InlineStack alignBlock="stretch" className={styles.masqueradeBannerContent}>
          {/* Mode toggle */}
          <Flex
            alignItems="center"
            px="200"
            py="125"
            flexShrink="0"
            className={`${styles.masqueradeBannerItem} ${styles.masqueradeBannerModeToggle}`}
          >
            <Tooltip
              isDisabled={user.can_masquerade_write}
              content="You don't have edit access to this account"
              isCustom={true}
            >
              {(context) => (
                <Toggle
                  data-test-read-only-masq
                  label="Edit mode"
                  isChecked={!masq.readOnly}
                  onChange={handleReadOnlyChange}
                  size="sm"
                  isDisabled={!user.can_masquerade_write}
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                />
              )}
            </Tooltip>
          </Flex>

          {/* Data display or account form */}
          <InlineStack
            aria-live="polite"
            alignBlock="stretch"
            flexShrink="0"
            flexGrow="1"
            className={styles.masqueradeBannerItem}
          >
            {isAccountFormVisible ? (
              <InlineStack alignBlock="center" flexGrow="1">
                <MasqForm autoFocus inputLabel="Masquerade as:" onMasquerade={masq.masqueradeInto} />
                <Flex alignItems="center" pr="100">
                  <Button
                    aria-label="Close"
                    variant="subtle"
                    icon="close"
                    isIconOnly
                    onClick={hideAccountForm}
                    size="sm"
                  />
                </Flex>
              </InlineStack>
            ) : (
              <InlineStack alignBlock="stretch" alignInline="space-between" flexGrow="1">
                <InlineStack alignBlock="stretch">
                  <Tooltip
                    content={
                      account.ordway_id != null
                        ? `Billing managed by Ordway (${account.ordway_id})`
                        : 'Billing managed by Stripe'
                    }
                    isCustom={true}
                  >
                    {(context) => (
                      <MasqLink
                        route="/settings"
                        label="Account"
                        value={String(account.id)}
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                      />
                    )}
                  </Tooltip>

                  <MasqLink route="/settings/billing/your-plan" label="State" value={account.account_state} />

                  <MasqLink route="/settings/privacy" label="Data center" value={dataCenterDisplay} />

                  {workspace != null ? (
                    <>
                      <MasqLink
                        route={generatePath('/workspaces/:workspaceId/settings', { workspaceId: workspace.id })}
                        label="Shard"
                        value={workspaceDetails.isLoading ? 'loading' : (workspaceDetails.data?.shard ?? 'N/A')}
                        icon="workspaces"
                      />

                      <MasqLink
                        route={generatePath('/workspaces/:workspaceId/settings/actions/email/smtp', {
                          workspaceId: workspace.id,
                        })}
                        label="SMTP"
                        value={smtpValue}
                        icon="workspaces"
                      />

                      <MasqLink
                        route={generatePath('/workspaces/:workspaceId/settings/edit', { workspaceId: workspace.id })}
                        label="Identifiers"
                        value={getIdentifiersDisplay(workspaceDetails.data)}
                        icon="workspaces"
                      />
                    </>
                  ) : null}

                  {/* Support Access */}
                  <MasqAccessPopover
                    accessSource={AccessSource.TS}
                    grantedOn={account.access_granted_on}
                    grantedById={account.access_granted_by}
                    grantedByEmail={resolveUser(account.access_granted_by)}
                    grantedUntil={account.access_granted_until}
                    masqedInUser={user}
                    teamAccessGranted={account.support_team_access_enabled}
                  >
                    <MasqLink
                      data-test-masq-access-popover
                      route="/settings/privacy"
                      label="Support access"
                      value={supportAccessValue}
                      icon="lock-unsecure"
                    />
                  </MasqAccessPopover>

                  {/* Success Access (only if account has CSM) */}
                  {account.has_csm ? (
                    <MasqAccessPopover
                      accessSource={AccessSource.CS}
                      grantedOn={account.cs_access_granted_on}
                      grantedById={account.cs_access_granted_by}
                      grantedByEmail={resolveUser(account.cs_access_granted_by)}
                      grantedUntil={account.cs_access_granted_until}
                      masqedInUser={user}
                      teamAccessGranted={account.success_team_access_enabled}
                    >
                      <MasqLink
                        data-test-masq-access-popover
                        route="/settings/privacy"
                        label="Success access"
                        value={successAccessValue}
                        icon="lock-unsecure"
                      />
                    </MasqAccessPopover>
                  ) : null}

                  <Stack px="150" py="050" justifyContent="center">
                    <TextLabel as="div" variant="secondary">
                      Flightdeck
                    </TextLabel>
                    <InlineStack gap="050" alignBlock="center">
                      <Link
                        href={`${FLIGHTDECK_BASE_URL}/accounts/${account.id}/details`}
                        isExternal
                        variant="secondary"
                      >
                        Account
                      </Link>
                      {workspace != null ? (
                        <>
                          <TextLabel as="div" variant="secondary">
                            |
                          </TextLabel>
                          <Link
                            href={`${FLIGHTDECK_BASE_URL}/workspaces/${workspace.id}/details`}
                            isExternal
                            variant="secondary"
                          >
                            Workspace
                          </Link>
                        </>
                      ) : null}
                    </InlineStack>
                  </Stack>
                </InlineStack>

                <InlineStack alignBlock="center" gap="100" pr="100">
                  <Button
                    data-test-masquerade-banner-change-account
                    variant="secondary"
                    size="sm"
                    onClick={showAccountForm}
                    flexShrink="0"
                  >
                    Change account
                  </Button>
                </InlineStack>
              </InlineStack>
            )}
          </InlineStack>

          {/* Exit masq button */}
          <Flex alignItems="center" px="100" py="100" flexShrink="0" className={styles.masqueradeBannerItem}>
            <Button data-test-masquerade-banner-exit variant="secondary" isDanger size="sm" onClick={masq.unmasquerade}>
              Exit masq
            </Button>
          </Flex>
        </InlineStack>
      </Box>
    );
  }

  return (
    <Box px="200" py="100" className={styles.masqueradeBannerAllowed} data-test-masquerade-banner="allowed">
      <MasqForm inputLabel="Masquerade as:" onMasquerade={masq.masqueradeInto} />
    </Box>
  );
}
